/**
 * A saved template's own declared variables, and the values a test call
 * supplies for them.
 *
 * This exists because of a real failure (docs/PROGRESS.md Slice DI). The
 * backend has carried per-call variables all along — adhoc_calls.variables,
 * read by cmd/worker's dispatcher into the flow's VariableStore — but
 * placeAdhocCall never sent any. A template declaring a required variable
 * therefore could not be dialled from the portal at all: the run refused to
 * start (flow/variables.go RequireSet, which is correct — plan 06 says a
 * missing value must never be spoken as literal braces), the switch hung up
 * about eleven seconds in, and the only trace was a worker log line.
 *
 * Reading the declarations lets the form ask for exactly what the template
 * needs and refuse to submit without it, so the same mistake is a form
 * error instead of a dropped call.
 */

export type VariableType = "string" | "number" | "boolean";

export interface VariableDecl {
  name: string;
  type: VariableType;
  required: boolean;
}

/** A coerced value, in the shape POST /v1/adhoc-calls takes. */
export type VariableValue = string | number | boolean;

const TYPES: VariableType[] = ["string", "number", "boolean"];

/**
 * Reads the `variables` block out of a published program.
 *
 * Deliberately defensive rather than cast-and-hope: `program` arrives as
 * `unknown` from the API, and a template published by an older schema
 * version may not carry the block at all. An entry that does not match the
 * schema is skipped rather than throwing, because one malformed
 * declaration must not make an otherwise dialable template unusable.
 */
export function declaredVariables(program: unknown): VariableDecl[] {
  if (typeof program !== "object" || program === null) return [];
  const raw = (program as { variables?: unknown }).variables;
  if (!Array.isArray(raw)) return [];
  const out: VariableDecl[] = [];
  for (const entry of raw) {
    if (typeof entry !== "object" || entry === null) continue;
    const { name, type, required } = entry as Record<string, unknown>;
    if (typeof name !== "string" || name === "") continue;
    if (typeof type !== "string" || !TYPES.includes(type as VariableType)) continue;
    out.push({ name, type: type as VariableType, required: required === true });
  }
  return out;
}

/** The required variables the form has no value for yet, in declared order. */
export function missingRequired(decls: VariableDecl[], entered: Record<string, string>): string[] {
  return decls.filter((d) => d.required && (entered[d.name] ?? "").trim() === "").map((d) => d.name);
}

export type CoerceResult =
  | { ok: true; values: Record<string, VariableValue> }
  | { ok: false; message: string };

/**
 * Turns the form's text inputs into the types the template declared.
 *
 * A blank OPTIONAL variable is omitted entirely rather than sent as "": the
 * flow's own RequireSet distinguishes "has no value" from "is empty", and
 * an empty string would satisfy a requirement the caller never actually
 * met. A blank REQUIRED variable is reported by missingRequired before
 * this runs.
 */
export function coerceVariables(decls: VariableDecl[], entered: Record<string, string>): CoerceResult {
  const values: Record<string, VariableValue> = {};
  for (const decl of decls) {
    const raw = (entered[decl.name] ?? "").trim();
    if (raw === "") {
      if (decl.required) return { ok: false, message: `${decl.name} is required` };
      continue;
    }
    switch (decl.type) {
      case "number": {
        const n = Number(raw);
        if (!Number.isFinite(n)) return { ok: false, message: `${decl.name} must be a number` };
        values[decl.name] = n;
        break;
      }
      case "boolean": {
        const lowered = raw.toLowerCase();
        if (lowered !== "true" && lowered !== "false") {
          return { ok: false, message: `${decl.name} must be true or false` };
        }
        values[decl.name] = lowered === "true";
        break;
      }
      default:
        values[decl.name] = raw;
    }
  }
  return { ok: true, values };
}
